<template>
  <Accordion
    :class="`accordion-${size}`"
    v-bind="$attrs"
  >
    <!-- eslint-disable vue/attribute-hyphenation -->
    <!-- primevue is not accepting hyphenation naming for props. It is accepting camelCase named props only -->
    <AccordionTab
      v-for="tab in tabs"
      :key="tab"
      v-bind="getPropsObj(tab)"
    >
      <template #header>
        <component
          :is="tab.children.header"
          v-if="tab.children.header"
        />
        <div
          v-else
          class="accordion-header"
        >
          <div
            class="accordion-header-text"
            :class="headerText"
          >
            {{ tab.props && tab.props.header }}
            <div
              v-if="tab.props && tab.props['sub-text' || 'subText']"
              class="accordion-header-sub-text small"
            >
              {{ tab.props["sub-text" || "subText"] }}
            </div>
          </div>
          <div class="accordion-right-wrap">
            <span
              v-if="tab.props && tab.props['sub-title' || 'subTitle']"
              class="accordion-header-sub-title small"
            >
              {{ tab.props["sub-title" || "subTitle"] }}
            </span>
            <Icon
              class="accordion-header-icon-open"
              :icon="csgAccordionMinus"
            />
            <Icon
              class="accordion-header-icon-closed"
              :icon="csgAccordionPlus"
            />
          </div>
        </div>
      </template>
      <component :is="tab.children.default" />
    </AccordionTab>
  </Accordion>
</template>

<script>
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { csgAccordionMinus, csgAccordionPlus } from '@csgSvgIcons/icons';
import Icon from '../icon';

export default {
  name: 'AccordionWrapper',
  components: {
    Accordion,
    AccordionTab,
    Icon,
  },
  props: {
    size: {
      type: String,
      validator: (val) => ['small', 'medium', 'large'].includes(val),
      default: 'small',
    },
  },
  data() {
    return {
      csgAccordionMinus,
      csgAccordionPlus,
    };
  },
  computed: {
    headerText() {
      return `accordion-header-text-${this.size}`;
    },
    tabs() {
      const tabs = [];
      this.$slots.default().forEach((child) => {
        if (this.isAccordionTab(child)) {
          tabs.push(child);
        } else if (child.children && child.children instanceof Array) {
          child.children.forEach((nestedChild) => {
            if (this.isAccordionTab(nestedChild)) {
              tabs.push(nestedChild);
            }
          });
        }
      });
      return tabs;
    },
  },
  methods: {
    isAccordionTab(child) {
      return child.type.name === 'AccordionTabWrapper';
    },
    getPropsObj(tab) {
      if (!tab.props) {
        return {};
      }
      return {
        disabled: tab.props.disabled,
        headerClass: tab.props['header-class'],
        headerStyle: tab.props['header-style'],
        contentStyle: tab.props['content-style'],
        contentClass: tab.props['content-class'],
        headerProps: tab.props['header-props'],
        headerActionProps: tab.props['header-action-props'],
        contentProps: tab.props['content-props'],
        pt: tab.props.pt,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.p-accordion {
  :deep(.p-accordion-header .p-accordion-header-link) {
    &[aria-expanded="true"] {
      border-bottom: 0;
    }
  }
  :deep(.p-accordion-header .p-accordion-header-link .p-accordion-toggle-icon) {
    display: none;
  }
  :deep(.p-accordion-tab:not(:first-child)
      .p-accordion-header
      .p-accordion-header-link) {
    border-top: 0 none;
  }
  .accordion-header-text {
    min-width: rem-calc(144);
  }
}

.p-accordion.accordion-small {
  :deep(.p-accordion-header-link) {
    padding: rem-calc(16) 0;
  }
  :deep(.p-accordion-content) {
    padding: 0 0 rem-calc(16) 0;
  }
}

.p-accordion.accordion-medium {
  :deep(.p-accordion-header-link) {
    padding: rem-calc(24) 0;
    @include breakpoint(medium down) {
      padding: rem-calc(16) 0;
    }
  }
  :deep(.p-accordion-content) {
    padding: 0 0 rem-calc(24) 0;
    @include breakpoint(medium down) {
      padding: 0 0 rem-calc(16) 0;
    }
  }
}

.p-accordion.accordion-large {
  :deep(.p-accordion-header-link) {
    padding: rem-calc(32) 0;
    @include breakpoint(medium down) {
      padding: rem-calc(24) 0;
      letter-spacing: $h6-letter-spacing-medium;
    }
  }
  :deep(.p-accordion-content) {
    padding: 0 0 rem-calc(32) 0;
    @include breakpoint(medium down) {
      padding: 0 0 rem-calc(24) 0;
    }
  }
}

.accordion-right-wrap {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.accordion-header-sub-title {
  margin-right: rem-calc(12);
  text-align: right;
  color: $gray-1-color;
}

.accordion-header-sub-text {
  color: $gray-1-color;
}

.p-accordion :deep(.p-accordion-tab) {
  &:not(:last-child) {
    border-bottom: 1px solid $gray-2-color;
  }
}

.accordion-header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.accordion-header-icon-open,
.accordion-header-icon-closed {
  min-width: rem-calc(24);
}

[aria-expanded="true"] {
  .accordion-header-icon-open {
    display: block;
  }

  .accordion-header-icon-closed {
    display: none;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "@core/csg/src/scss/typography/typography-mixins.scss";
.accordion-header-icon-open {
  display: none;
}

.accordion-header-text-small {
  @include body(heavy);
}
.accordion-header-text-medium {
  @include body(large);
}
.accordion-header-text-large {
  @include header(h6);
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "@core/csg/src/scss/typography/typography-mixins.scss";
.accordion-header-icon-open {
  display: none;
}

.accordion-header-text-small {
  @include eyebrow;
}
.accordion-header-text-medium {
  @include label;
}
.accordion-header-text-large {
  @include header(h6);
}
</style>
